import React, { useEffect, useRef, useState } from "react";
import { Dialog } from "primereact/dialog";
import { Password } from "primereact/password";
import { Button } from "primereact/button";
import { handleApiError, handleApiSuccess } from "./apiErrorHandler";
import axiosInstance from "../services/axiosInstance";

/**
 * OperatorSecurityDialog
 *
 * Props:
 *  - visible (boolean)
 *  - onHide () => void
 *  - orgSettings (custom_json_settings from org)
 *
 * Purpose:
 *  - Allow logged-in user to:
 *      • Change their own operator PIN
 *      • Regenerate their own operator QR
 */
const OperatorSecurityDialog = ({ visible, onHide, orgSettings = {} }) => {

  const [newPin, setNewPin] = useState("");
  const [loadingPin, setLoadingPin] = useState(false);
  const [loadingQr, setLoadingQr] = useState(false);

  const operatorCfg = orgSettings?.operator_authentication || {};

  const operatorAuthEnabled = !!operatorCfg.enabled;
  const allowPin = operatorCfg.methods?.includes("PIN");
  const allowQr = operatorCfg.methods?.includes("QR");
  const allowUserPinChange =
    operatorCfg.allow_user_pin_change !== false;

  useEffect(() => {
    if (!visible) {
      setNewPin("");
      setLoadingPin(false);
      setLoadingQr(false);
    }
  }, [visible]);

  const handlePinSave = async () => {
    if (!newPin) return;

    try {
      setLoadingPin(true);
      await axiosInstance.post("/api/operator/set-pin/", {
        pin: newPin, // self reset
      });
      handleApiSuccess("PIN Updated", "Your operator PIN has been updated");
      setNewPin("");
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoadingPin(false);
    }
  };

  const handleQrRegenerate = async () => {
    try {
      setLoadingQr(true);
      await axiosInstance.post("/api/operator/regenerate-qr/");
      handleApiSuccess(
        "QR Regenerated",
        "Your operator QR code has been regenerated"
      );
    } catch (err) {
      handleApiError(err);
    } finally {
      setLoadingQr(false);
    }
  };

  return (
    <>
      <Dialog
        header="Operator Security"
        visible={visible}
        modal
        style={{ width: "28rem" }}
        onHide={onHide}
        draggable={false}
      >
        {!operatorAuthEnabled && (
          <div className="text-center text-gray-500">
            Operator authentication is not enabled for your organization.
          </div>
        )}

        {operatorAuthEnabled && (
          <div className="p-fluid">
            {/* PIN SECTION */}
            {allowPin && allowUserPinChange && (
              <>
                <div className="field">
                  <label className="font-bold">
                    Change Operator PIN
                  </label>
                  <Password
                    value={newPin}
                    onChange={(e) => setNewPin(e.target.value)}
                    feedback={false}
                    maxLength={4}
                    toggleMask
                    placeholder="Enter new PIN"
                    inputStyle={{ letterSpacing: "3px" }}
                  />
                </div>

                <div className="flex justify-content-end mt-2">
                  <Button
                    label="Save PIN"
                    icon="pi pi-check"
                    onClick={handlePinSave}
                    loading={loadingPin}
                    disabled={!newPin}
                  />
                </div>

                <hr className="my-3" />
              </>
            )}

            {/* QR SECTION */}
            {allowQr && (
              <div className="flex justify-content-between align-items-center">
                <div>
                  <div className="font-bold">Operator QR</div>
                  <small className="text-gray-500">
                    Used for quick operator identification
                  </small>
                </div>

                <Button
                  label="Regenerate QR"
                  icon="pi pi-refresh"
                  className="p-button-warning p-button-sm"
                  onClick={handleQrRegenerate}
                  loading={loadingQr}
                />
              </div>
            )}

            {!allowPin && !allowQr && (
              <div className="text-center text-red-500">
                No operator authentication methods enabled.
              </div>
            )}
          </div>
        )}
      </Dialog>
    </>
  );
};

export default OperatorSecurityDialog;
